// =============================================================================
// MeuDriver — Q2: Hook de histórico detalhado do estudante
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ─── Tipos ───────────────────────────────────────────────────────────────────

export interface StudentStats {
  total_lessons: number
  total_hours: number
  total_spent: number
  unique_instructors: number
  avg_rating_given: number | null
  first_lesson_at: string | null
  last_lesson_at: string | null
  lessons_this_month: number
  lessons_this_year: number
}

export interface StudentLessonHistoryRow {
  id: string
  scheduled_at: string
  booking_type: string
  total_price: number | null
  status: string
  instructor_first_name: string | null
  instructor_avatar_url: string | null
}

export interface StudentLessonHistoryResult {
  total: number
  rows: StudentLessonHistoryRow[]
}

export interface StudentMonthlyLesson {
  month_label: string  // 'YYYY-MM'
  lesson_count: number
}

// ─── Chaves de query ─────────────────────────────────────────────────────────

const KEYS = {
  stats:   ['student_history', 'stats'] as const,
  history: (page: number) => ['student_history', 'list', page] as const,
  monthly: ['student_history', 'monthly'] as const,
}

const PAGE_SIZE = 10

// ─── useStudentStats ──────────────────────────────────────────────────────────

/**
 * KPIs do estudante autenticado via RPC get_student_stats.
 */
export function useStudentStats() {
  return useQuery({
    queryKey: KEYS.stats,
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<StudentStats> => {
      const { data, error } = await supabase.rpc('get_student_stats')
      if (error) throw new Error(error.message)
      return data as StudentStats
    },
  })
}

// ─── useStudentLessonHistory ──────────────────────────────────────────────────

/**
 * Histórico paginado de aulas concluídas do estudante autenticado.
 */
export function useStudentLessonHistory(page = 1) {
  return useQuery({
    queryKey: KEYS.history(page),
    staleTime: 60_000,
    queryFn: async (): Promise<StudentLessonHistoryResult> => {
      const { data, error } = await supabase.rpc('get_student_lesson_history', {
        p_limit:  PAGE_SIZE,
        p_offset: (page - 1) * PAGE_SIZE,
      })
      if (error) throw new Error(error.message)
      return data as StudentLessonHistoryResult
    },
  })
}

// ─── useStudentMonthlyLessons ─────────────────────────────────────────────────

/**
 * Contagem de aulas por mês (últimos 12 meses) para o gráfico de barras.
 */
export function useStudentMonthlyLessons() {
  return useQuery({
    queryKey: KEYS.monthly,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<StudentMonthlyLesson[]> => {
      const { data, error } = await supabase.rpc('get_student_lessons_by_month')
      if (error) throw new Error(error.message)
      return (data ?? []) as StudentMonthlyLesson[]
    },
  })
}

export const STUDENT_HISTORY_PAGE_SIZE = PAGE_SIZE
